import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikControl from "components/FormikControl/FormikControl";
import { Button, Grid, Paper } from "@material-ui/core";
import PropTypes from "prop-types";
import { breakDownModules, buildUpModules, fetchDataFromApi } from "utils";
import useStyles from "assets/jss/material-dashboard-react/views/roleFormStyle";
import ClearIcon from "@material-ui/icons/Clear";
import eightAxios, { BASE_URL } from "configs/axiosConfig";
import CheckboxTree from "react-checkbox-tree";
import "assets/css/tree.css";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { ROLES_URL, URLS } from "configs/constants";
import { useHistory, useParams } from "react-router-dom";
import LoadingIcon from "components/LoadingIcon/LoadingIcon";

import { useTranslation } from "react-i18next";
import { buildCssTree } from "./treeData";

const RoleViewDetails = () => {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();
  const { t } = useTranslation();
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [modules, setModules] = useState([]);
  const [expanded, setExpanded] = useState(["Configuration"]);
  const [cssTree, setCssTree] = useState([]);

  if (params.id) {
    useEffect(() => {
      fetchDataFromApi(`${ROLES_URL}${params.id}/`, setRoleToEdit);
    }, []);
  }

  const initialValues = roleToEdit
    ? roleToEdit
    : {
        is_active: true,
        mobile_access: true,
        name: "",
        description: "",
      };

  const [isActive, setIsActive] = useState(initialValues.is_active);

  useEffect(() => {
    if (roleToEdit) {
      if (!modules.length) {
        setModules(breakDownModules(roleToEdit.modules_json));
      }
    }
  }, [roleToEdit]);

  useEffect(() => {
    eightAxios
      .get(BASE_URL + "configuration/modules/")
      .then((res) => {
        let data = buildCssTree(res.data.modules, res.data.pages);
        setCssTree(data);
      })
      .catch((err) => {
        console.error(err);
        setCssTree([]);
      });
  }, []);

  const handleChecked = (checked) => {
    setModules(
      checked.filter((item) => {
        return !item.startsWith("__");
      })
    );
  };

  const handleSubmit = (values, { setSubmitting }) => {
    values["modules_json"] = buildUpModules(modules);
    if (values.id === undefined) {
      eightAxios
        .post(ROLES_URL, values)
        .then(() => {
          setSubmitting(false);
          history.push(URLS.ROLE_LIST);
        })
        .catch(() => {
          setSubmitting(false);
          history.push(URLS.ROLE_LIST);
        });
    } else {
      eightAxios
        .put(`${ROLES_URL}${params.id}/`, values)
        .then(() => {
          setSubmitting(false);
          history.push(URLS.ROLE_LIST);
        })
        .catch(() => {
          setSubmitting(false);
          history.push(URLS.ROLE_LIST);
        });
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Role Name Required"),
  });

  // eslint-disable-next-line no-unused-vars
  const onSwitchChanged = () => {
    setIsActive(!isActive);
  };

  if (params.id && !roleToEdit) {
    return <LoadingIcon text="Loading" image />;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {() => {
        return (
          <>
            <Paper className={classes.topBanner}>
              Role: {params.id ? "View Details" : "Create"}
            </Paper>
            <Paper className={classes.filterPaper} variant="outlined">
              <Form style={{ width: "100%" }}>
                <Grid
                  container
                  spacing={1}
                  alignContent="center"
                  justify="space-between"
                >
                  <Grid item xs={6} sm={6} md={2} lg={3}>
                    <FormikControl
                      name="is_active"
                      key="is_active"
                      control="renderonly"
                      label="Is Active?"
                      valueType="boolean"
                      yesValue="Yes"
                      noValue="No"
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={3}>
                    <FormikControl
                      name="mobile_access"
                      key="mobile_access"
                      control="renderonly"
                      label="Mobile Access?"
                      valueType="boolean"
                      yesValue="Yes"
                      noValue="No"
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={8} lg={3}>
                    <FormikControl
                      control="renderonly"
                      name="name"
                      key="name"
                      label="Role Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={8} lg={3}>
                    <FormikControl
                      control="renderonly"
                      name="description"
                      key="description"
                      label="Description"
                    />
                  </Grid>

                  <Grid item container direction="column" xs={12}>
                    <Paper className={classes.topBanner}>
                      {t("Task Action")}
                    </Paper>
                    <div className={classes.treeContainer}>
                      <CheckboxTree
                        checkModel="all"
                        nodes={cssTree}
                        disabled
                        checked={modules}
                        expanded={expanded}
                        onCheck={(checked) => handleChecked(checked)}
                        onExpand={(expanded) => setExpanded(expanded)}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  direction="row"
                  className={classes.buttonContainer}
                >
                  <Button
                    startIcon={<ClearIcon />}
                    variant="contained"
                    color="secondary"
                    className={classes.cancel}
                    onClick={() => history.push(URLS.ROLE_LIST)}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Form>
            </Paper>
          </>
        );
      }}
    </Formik>
  );
};

RoleViewDetails.propTypes = {
  data: PropTypes.object,
  switchToList: PropTypes.func,
};

export default RoleViewDetails;
